import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import RegistrationProgress from './components/RegistrationProgress';
import PersonalInfoForm from './components/PersonalInfoForm';
import MembershipPlanCard from './components/MembershipPlanCard';
import PaymentMethodForm from './components/PaymentMethodForm';
import WaiverForm from './components/WaiverForm';
import RegistrationSummary from './components/RegistrationSummary';
import { gymService, paymentService } from '../../lib/services';
import { supabase } from '../../lib/supabase';

const MemberRegistration = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [gyms, setGyms] = useState([]);
  const [selectedGymId, setSelectedGymId] = useState(null);
  const [paymentReference, setPaymentReference] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const [personalInfo, setPersonalInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: '',
    emergencyName: '',
    emergencyPhone: '',
    emergencyRelationship: '',
    address: '',
    city: '',
    state: '',
    zipCode: ''
  });

  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState({
    paymentMethod: 'card',
    cardholderName: '',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    accountHolderName: '',
    accountNumber: '',
    routingNumber: '',
    zelleContact: '',
    zelleConfirmationNumber: ''
  });

  // Fetch gyms on mount
  useEffect(() => {
    loadGyms();
  }, []);

  const loadGyms = async () => {
    const { data, error } = await gymService?.getActiveGyms();
    if (!error && data) {
      setGyms(data);
    }
  };

  const membershipPlans = [
    {
      id: 'monthly',
      name: 'Monthly Membership',
      description: 'Flexible month-to-month access',
      price: 49,
      billingCycle: 'month',
      trialDays: 7,
      features: [
        { text: 'Unlimited gym access', included: true },
        { text: 'All group fitness classes', included: true },
        { text: 'Locker room & showers', included: true },
        { text: 'Free WiFi', included: true },
        { text: 'Personal training sessions', included: false },
        { text: 'Nutrition consultation', included: false }
      ]
    },
    {
      id: 'annual',
      name: 'Annual Membership',
      description: 'Best value with annual commitment',
      price: 39,
      originalPrice: 49,
      billingCycle: 'month',
      trialDays: 14,
      features: [
        { text: 'Unlimited gym access', included: true },
        { text: 'All group fitness classes', included: true },
        { text: 'Locker room & showers', included: true },
        { text: 'Free WiFi', included: true },
        { text: '2 personal training sessions/month', included: true },
        { text: 'Nutrition consultation', included: true },
        { text: 'Guest passes (2/month)', included: true }
      ]
    },
    {
      id: 'premium',
      name: 'Premium Membership',
      description: 'All-inclusive fitness experience',
      price: 79,
      billingCycle: 'month',
      trialDays: 14,
      features: [
        { text: 'Unlimited gym access', included: true },
        { text: 'All group fitness classes', included: true },
        { text: 'Locker room & showers', included: true },
        { text: 'Free WiFi', included: true },
        { text: 'Unlimited personal training', included: true },
        { text: 'Nutrition consultation', included: true },
        { text: 'Guest passes (4/month)', included: true },
        { text: 'Massage therapy (2/month)', included: true },
        { text: 'Priority class booking', included: true }
      ]
    }
  ];

  const selectedPlan = membershipPlans?.find(plan => plan?.id === selectedPlanId);

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!personalInfo?.firstName?.trim()) newErrors.firstName = 'First name is required';
      if (!personalInfo?.lastName?.trim()) newErrors.lastName = 'Last name is required';
      if (!personalInfo?.email?.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/?.test(personalInfo?.email)) {
        newErrors.email = 'Email is invalid';
      }
      if (!personalInfo?.phone?.trim()) newErrors.phone = 'Phone number is required';
      if (!personalInfo?.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
      if (!personalInfo?.emergencyName?.trim()) newErrors.emergencyName = 'Emergency contact name is required';
      if (!personalInfo?.emergencyPhone?.trim()) newErrors.emergencyPhone = 'Emergency contact phone is required';
      if (!selectedGymId) newErrors.gym = 'Please select a gym location';
    }

    if (step === 2) {
      if (!selectedPlanId) newErrors.plan = 'Please select a membership plan';
    }

    if (step === 3) {
      if (paymentInfo?.paymentMethod === 'card') {
        if (!paymentInfo?.cardholderName?.trim()) newErrors.cardholderName = 'Cardholder name is required';
        if (!paymentInfo?.cardNumber?.trim()) {
          newErrors.cardNumber = 'Card number is required';
        } else if (paymentInfo?.cardNumber?.replace(/\s/g, '')?.length !== 16) {
          newErrors.cardNumber = 'Card number must be 16 digits';
        }
        if (!paymentInfo?.expiryDate?.trim()) newErrors.expiryDate = 'Expiry date is required';
        if (!paymentInfo?.cvv?.trim()) {
          newErrors.cvv = 'CVV is required';
        } else if (paymentInfo?.cvv?.length !== 3) {
          newErrors.cvv = 'CVV must be 3 digits';
        }
      } else if (paymentInfo?.paymentMethod === 'pay_at_gym') {
        // No validation needed for pay at gym
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = async () => {
    if (validateStep(currentStep)) {
      // Generate payment reference if pay_at_gym selected and moving from step 3
      if (currentStep === 3 && paymentInfo?.paymentMethod === 'pay_at_gym' && !paymentReference) {
        setIsProcessing(true);
        const { data, error } = await paymentService?.generatePaymentReference(
          selectedPlan?.price,
          selectedGymId
        );
        setIsProcessing(false);
        
        if (error) {
          setErrors({ payment: 'Failed to generate payment reference. Please try again.' });
          return;
        }
        
        setPaymentReference(data);
      }

      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleWaiverAccept = async (waiverData) => {
    if (!waiverData?.waiverAccepted || !waiverData?.parqAccepted || !waiverData?.signature) {
      const newErrors = {};
      if (!waiverData?.waiverAccepted) newErrors.waiverAccepted = 'You must accept the liability waiver';
      if (!waiverData?.parqAccepted) newErrors.parqAccepted = 'You must complete the PAR-Q';
      if (!waiverData?.signature) newErrors.signature = 'Electronic signature is required';
      setErrors(newErrors);
      return;
    }

    setIsProcessing(true);

    try {
      // Create user account
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email: personalInfo?.email,
        password: 'TempPassword123!', // Should be collected in form
        options: {
          data: {
            first_name: personalInfo?.firstName,
            last_name: personalInfo?.lastName,
            full_name: `${personalInfo?.firstName} ${personalInfo?.lastName}`,
            role: 'member'
          }
        }
      });

      if (authError) throw authError;

      // Update user profile with gym and payment info
      const { error: profileError } = await supabase?.from('user_profiles')?.update({
          gym_id: selectedGymId,
          account_status: (paymentInfo?.paymentMethod === 'pay_at_gym' || paymentInfo?.paymentMethod === 'zelle') ? 'pending' : 'active',
          phone: personalInfo?.phone,
          ...(paymentReference && { payment_reference_id: paymentReference?.id })
        })?.eq('id', authData?.user?.id);

      if (profileError) throw profileError;

      setIsProcessing(false);
      navigate('/member-dashboard');
    } catch (error) {
      setIsProcessing(false);
      setErrors({ submit: error?.message || 'Registration failed. Please try again.' });
    }
  };

  const handleEditStep = (step) => {
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Dumbbell" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                  TrainaryGym
                </h1>
                <p className="text-xs text-muted-foreground">Member Registration</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={() => navigate('/login')}
              iconName="ArrowLeft"
              iconPosition="left"
            >
              Back to Login
            </Button>
          </div>
        </div>
      </header>
      <main className="container mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
        <div className="max-w-7xl mx-auto">
          <RegistrationProgress currentStep={currentStep} totalSteps={4} />

          <div className="mt-8 md:mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            <div className="lg:col-span-2 space-y-6 md:space-y-8">
              {currentStep === 1 && (
                <>
                  <PersonalInfoForm
                    formData={personalInfo}
                    errors={errors}
                    onChange={setPersonalInfo}
                  />
                  
                  <div className="bg-card rounded-xl p-6 md:p-8 shadow-md border border-border">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name="MapPin" size={20} color="var(--color-primary)" />
                      </div>
                      <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                        Select Your Gym Location
                      </h2>
                    </div>
                    {errors?.gym && (
                      <div className="mb-4 bg-error/10 border border-error rounded-lg p-4 flex items-center gap-3">
                        <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                        <p className="text-sm text-error">{errors?.gym}</p>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {gyms?.map((gym) => (
                        <button
                          key={gym?.id}
                          onClick={() => setSelectedGymId(gym?.id)}
                          className={`
                            p-4 rounded-lg border-2 text-left transition-all duration-250
                            ${selectedGymId === gym?.id
                              ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                            }
                          `}
                        >
                          <h3 className="font-semibold text-foreground mb-2">{gym?.name}</h3>
                          <p className="text-sm text-muted-foreground">{gym?.address}</p>
                          <p className="text-sm text-muted-foreground">
                            {gym?.city}, {gym?.state} {gym?.zip_code}
                          </p>
                          {gym?.phone && (
                            <p className="text-sm text-muted-foreground mt-2">{gym?.phone}</p>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {currentStep === 2 && (
                <div className="space-y-6">
                  <div className="bg-card rounded-xl p-6 md:p-8 shadow-md border border-border">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon name="CreditCard" size={20} color="var(--color-primary)" />
                      </div>
                      <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                        Choose Your Membership
                      </h2>
                    </div>
                    {errors?.plan && (
                      <div className="mb-6 bg-error/10 border border-error rounded-lg p-4 flex items-center gap-3">
                        <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                        <p className="text-sm text-error">{errors?.plan}</p>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    {membershipPlans?.map((plan, index) => (
                      <MembershipPlanCard
                        key={plan?.id}
                        plan={plan}
                        isSelected={selectedPlanId === plan?.id}
                        onSelect={setSelectedPlanId}
                        isPopular={index === 1}
                      />
                    ))}
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <PaymentMethodForm
                  formData={paymentInfo}
                  errors={errors}
                  onChange={setPaymentInfo}
                  selectedPlan={selectedPlan}
                  paymentReference={paymentReference}
                  gymId={selectedGymId}
                />
              )}

              {currentStep === 4 && (
                <WaiverForm
                  onAccept={handleWaiverAccept}
                  errors={errors}
                  isProcessing={isProcessing}
                />
              )}

              <div className="flex items-center justify-between gap-4 pt-6 border-t border-border">
                {currentStep > 1 && (
                  <Button
                    variant="outline"
                    onClick={handleBack}
                    iconName="ArrowLeft"
                    iconPosition="left"
                  >
                    Back
                  </Button>
                )}
                {currentStep < 4 ? (
                  <Button
                    onClick={handleNext}
                    iconName="ArrowRight"
                    iconPosition="right"
                    className={currentStep === 1 ? 'ml-auto' : ''}
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    onClick={() => handleWaiverAccept({
                      waiverAccepted: true,
                      parqAccepted: true,
                      signature: 'signed'
                    })}
                    iconName="Check"
                    iconPosition="left"
                    className="ml-auto"
                  >
                    Complete Registration
                  </Button>
                )}
              </div>
            </div>

            <div className="lg:col-span-1">
              <RegistrationSummary
                currentStep={currentStep}
                personalInfo={personalInfo}
                selectedPlan={selectedPlan}
                selectedGym={gyms?.find(g => g?.id === selectedGymId)}
                paymentMethod={paymentInfo?.paymentMethod}
                paymentReference={paymentReference}
                onEditStep={handleEditStep}
              />
            </div>
          </div>

          <div className="mt-8 flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1 || isProcessing}
              iconName="ArrowLeft"
              iconPosition="left"
            >
              Back
            </Button>
            <Button
              onClick={handleNext}
              disabled={isProcessing}
              iconName="ArrowRight"
              iconPosition="right"
            >
              {isProcessing ? 'Processing...' : currentStep === 4 ? 'Complete Registration' : 'Continue'}
            </Button>
          </div>
        </div>
      </main>
      <footer className="bg-card border-t border-border mt-12 md:mt-16">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Icon name="Shield" size={20} color="var(--color-success)" />
              <p className="text-sm text-muted-foreground">
                Secure registration powered by SSL encryption
              </p>
            </div>
            <p className="text-sm text-muted-foreground">
              &copy; {new Date()?.getFullYear()} TrainaryGym. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MemberRegistration;